import React, {useEffect, useState} from 'react';
import TableActionButtons from "app/main/shared-components/base-table/TableActionButtons";
import {Column, TableActionsTypes} from "app/types/TableTypes";
import PageHeader from "app/main/shared-components/page-header/PageHeader";
import BaseTable from "app/main/shared-components/base-table/BaseTable";
import history from '@history';
import SearchInput from "../../shared-components/search-input/SearchInput";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "app/store";
import {fetchAllAircrafts} from "../store/aircrafts/aircrafts.slice";
import {useRouteMatch} from "react-router-dom";
import {toggleQuickPanel} from "../../shared-components/quick-panel/store";
import {Switch} from "@material-ui/core";
import {AircraftService} from "app/services";
import {AircraftStatus} from "app/types/service-types/AircraftTypes";
import PaginationBar from "../../shared-components/pagination-bar/PaginationBar";
import {warningMsg, successMsg, errorMsg} from "app/utils/MessageUtils";


const AircraftsPage = () => {
  const dispatch = useDispatch();
  let {url} = useRouteMatch();
  const {
    aircrafts,
    loading,
    pagination
  } = useSelector((state: AppState) => state.configurations.aircrafts.aircrafts);

  const [filteredAircrafts, setFilteredAircrafts] = useState(aircrafts || []);
  const [currentPage, setCurrentPage] = useState(0);

  const handleStatusToggle = async (aircraftId: string, currentStatus: AircraftStatus) => {
    const newStatus: AircraftStatus = currentStatus === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE';
    try {
      const response = await AircraftService.updateStatus(aircraftId, newStatus);
      
      // Show warning if there are affected future flights
      if (response.affectedFutureFlights && response.affectedFutureFlights > 0) {
        dispatch(warningMsg(
          `This aircraft has ${response.affectedFutureFlights} upcoming flight(s). They will continue to operate normally, but no new flights can be assigned to this aircraft.`
        ));
      } else {
        dispatch(successMsg(`Aircraft status updated to ${newStatus}`));
      }
      
      dispatch(fetchAllAircrafts({ page: currentPage }));
    } catch (err) {
      dispatch(errorMsg(err, 'Error updating aircraft status'));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch(fetchAllAircrafts({ page }));
  };

  const tableActions: TableActionsTypes = {
    onEditRow: (rowId: number) => {
      dispatch(toggleQuickPanel());
      history.push(`${url}/update/${rowId}`);
    },
    onViewRow: (rowId: string) => {
      history.push(`${url}/detail/${rowId}`);
    }
  }

  const columns: Column[] = [
    {
      Header: 'Code',
      accessor: 'code'
    },
    {
      Header: 'Name',
      accessor: 'name'
    },
    {
      Header: 'Total Rows',
      accessor: 'totalRows'
    },
    {
      Header: 'Columns',
      accessor: 'seatColumns',
      Cell: ({cell}: any) => <span>{cell.value ? cell.value.replace(/,/g, '') : '-'}</span>
    },
    {
      Header: 'Total Seats',
      accessor: 'totalSeats'
    },
    {
      Header: 'Layout',
      accessor: 'layoutDescription',
      Cell: ({cell}: any) => <span>{cell.value || '-'}</span>
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({cell}: any) => (
        <Switch
          checked={cell.value === 'AVAILABLE'}
          onChange={() => handleStatusToggle(cell.row.original.id, cell.value)}
          color="primary"
        />
      )
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({cell}: any) => (
        <TableActionButtons {...tableActions} rowId={cell.value} name={cell.row.original.name}/>
      )
    }
  ];

  const searchAircrafts = (value: string) => {
    if (value) {
      const filtered = aircrafts.filter((ac: any) => {
        return ac.code?.toLowerCase().match(value) ||
          ac.name?.toLowerCase().match(value) ||
          ac.layoutDescription?.toLowerCase()?.match(value);
      });
      setFilteredAircrafts(filtered);
    } else {
      setFilteredAircrafts(aircrafts);
    }
  }

  useEffect(() => {
    dispatch(fetchAllAircrafts({ page: 0 }));
  }, []);

  useEffect(() => {
    setFilteredAircrafts(aircrafts);
  }, [aircrafts]);

  return (
    <div>
      <PageHeader
        title="Aircraft Definitions"
        desc="Manage aircraft configurations and seat layouts"
        button="Register"
        onClickButton={() => {
          history.push(`${url}/create`);
          dispatch(toggleQuickPanel());
        }}
      />
      <div style={{marginTop: '10px', marginBottom: '10px'}}>
        <SearchInput onChange={searchAircrafts}/>
      </div>
      <BaseTable columns={columns} data={filteredAircrafts} isLoading={loading}/>
      {pagination.totalPages > 0 && (
        <PaginationBar 
          totalPages={pagination.totalPages} 
          currentPage={currentPage + 1}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default AircraftsPage;
